<template>
  <div
    @mouseleave="closeMouseOut"
    @mouseover="cancelCloseMouseOut"
    ref="wrapper"
    v-click-away="close"
    :data-opened="opened ? '' : null"
  >
    <slot name="btn" v-bind="{ opened, dropdown, open, close, toggle, btn }"></slot>
    <!--
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      @after-enter="showed"
      :leave-active-class="leaveAnimate.active"
      :leave-from-class="leaveAnimate.from"
      :leave-to-class="leaveAnimate.to"
    >
    -->
    <slot name="dropdown" v-bind="{ opened, dropdown, open, close, toggle }"></slot>
    <!--</transition>-->
  </div>
</template>

<script>
import { ref, computed, nextTick, watch, provide, inject } from 'vue'
import { createPopper } from '@popperjs/core'
import delay from 'lodash/delay'
import { isNumber, useElementSize } from '@vueuse/core'

let closeOpened = null // function for force close last opened dropdown wo animation

export default {
  emits: ['hide', 'show', 'update:modelValue'],
  props: {
    placement: {
      type: String,
      default: 'bottom',
    },
    offset: {
      type: Array,
      default: () => {
        return []
      },
    },
    hover: {
      type: [Boolean, Number],
      default: false,
    },
    hideOnClick: {
      type: Boolean,
      default: true,
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
    dropdownHasParent: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    // const dropdown = ref(null) // dropdown el
    let dropdown = null
    let btn = ref(null) // btn element
    const btnSize = useElementSize(btn)

    const opened = ref(props.modelValue) // is dropdown open?
    const animate = ref(true) // animate closing dropdown?
    let delayTimer // delay when dropdown close on mouse out
    let delayOpenTimer // delay when hover dropdown open
    let popper
    const wrapper = ref(null)
    let showed = false // show animation done?

    const injectedAwDropdownParent = inject('awDropdownParent', false)
    const hasDropdownParent = computed(() => (props.dropdownHasParent ? true : injectedAwDropdownParent))

    // closing transition by animate value
    const leaveAnimate = computed(() => {
      if (animate.value === false) {
        return {
          active: '',
          from: '',
          to: '',
        }
      } else {
        return {
          active: 'transition duration-100 ease-out',
          from: 'transform scale-100 opacity-100',
          to: 'transform scale-95 opacity-0',
        }
      }
    })

    const closeMouseOut = () => {
      if (document.activeElement && document.activeElement.tagName === 'INPUT' && dropdown) {
        let parent = document.activeElement.parentNode
        do {
          if (parent === dropdown) {
            return
          }
          parent = parent.parentNode
        } while (parent !== document)
      }

      if (props.hover && opened.value && showed) {
        if (delayTimer) {
          clearTimeout(delayTimer)
          delayTimer = null
        }

        delayTimer = delay(() => {
          // this.close()
          opened.value = false
          emit('hide')
          emit('update:modelValue', false)
        }, 500)
      } else if (delayOpenTimer) {
        clearTimeout(delayOpenTimer)
      }
    }

    const cancelCloseMouseOut = () => {
      if (props.hover && delayTimer) {
        clearTimeout(delayTimer)
        delayTimer = null
      }
    }

    const doOpen = () => {
      if (closeOpened && !hasDropdownParent.value) {
        closeOpened.call(null)
      }

      animate.value = true
      opened.value = true
      emit('show')
      emit('update:modelValue', true)

      // do popper job
      if (!popper) {
        popper = createPopper(wrapper.value, dropdown, {
          placement: props.placement,
          modifiers: [
            {
              name: 'preventOverflow',
              options: {},
            },
            {
              name: 'offset',
              options: {
                offset: props.offset,
              },
            },
            {
              name: 'computeStyles',
              options: {
                adaptive: true, // true by default
              },
            },
          ],
          onFirstUpdate: () => {},
        })
      }

      nextTick(() => {
        popper.update().then(() => {
          showed = true
        })
      })

      // create function to force close this dropdown
      if (!hasDropdownParent.value) {
        // dropdown nema dropdown parenta
        closeOpened = () => {
          if (opened.value) {
            animate.value = false
            opened.value = false
            emit('hide')
            emit('update:modelValue', false)
          }
          closeOpened = null
        }
      }
    }

    const open = () => {
      if (opened.value) return

      if (props.hover && isNumber(props.hover)) {
        delayOpenTimer = delay(doOpen, props.hover)
      } else {
        doOpen()
      }
    }

    const close = () => {
      if (opened.value === false) return
      opened.value = false
      emit('hide')
      emit('update:modelValue', false)
    }

    watch(
      () => props.modelValue,
      (val) => {
        if (val) {
          open()
        } else {
          close()
        }
      }
    )

    // placement
    watch(
      () => props.placement,
      (val) => {
        if (!popper) return

        popper.setOptions({ placement: val })
      }
    )

    // watch btn size change
    watch([btnSize.width, btnSize.height], () => {
      if (!showed || !popper || !btn.value) return
      popper.update()
    })

    provide('awDropdownParent', true)

    return {
      closeMouseOut,
      cancelCloseMouseOut,
      open,
      close,
      toggle() {
        if (!opened.value) {
          open()
        } else {
          close()
        }
      },
      opened,
      dropdown: (el) => {
        if (!el) return

        if (props.hover) {
          el.addEventListener('mouseover', cancelCloseMouseOut)
          el.addEventListener('mouseleave', closeMouseOut)
        }
        dropdown = el
      },
      btn: (el) => {
        if (!el) return
        btn.value = el
      },
      leaveAnimate,
      wrapper,
      showed() {
        showed = true
      },
    }
  },
}
</script>
